/**
 * Assembly Mode - Main Entry
 *
 * Free-form canvas for composing rotated components.
 */

import {
  AssemblyCanvas,
  getAssemblyComponents,
  rotateTriangle,
  translateTriangle,
  type AssemblyComponent,
  type AssemblyTriangle,
} from './core/assembly';
import { TRINITY_PALETTE } from './core/types';

// DOM Elements
const canvas = document.getElementById('canvas') as HTMLCanvasElement;
const ctx = canvas.getContext('2d')!;
const componentGrid = document.getElementById('component-grid') as HTMLDivElement;
const rotationSlider = document.getElementById('rotation-slider') as HTMLInputElement;
const rotationValue = document.getElementById('rotation-value') as HTMLSpanElement;
const clearBtn = document.getElementById('clear-btn') as HTMLButtonElement;
const exportSvgBtn = document.getElementById('export-svg-btn') as HTMLButtonElement;
const placedCount = document.getElementById('placed-count') as HTMLSpanElement;
const snapToggle = document.getElementById('snap-toggle') as HTMLInputElement;

// State
const assembly = new AssemblyCanvas(50);
let selectedComponent: AssemblyComponent | null = null;
let currentRotation = 0;
let mousePos = { x: 0, y: 0 };
let snapEnabled = true;
let canvasCenter = { x: 0, y: 0 };

// Resize handler
function handleResize() {
  const container = canvas.parentElement!;
  const dpr = window.devicePixelRatio || 1;
  const width = container.clientWidth;
  const height = container.clientHeight;

  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  ctx.scale(dpr, dpr);

  canvasCenter = { x: width / 2, y: height / 2 };
  render();
}

window.addEventListener('resize', handleResize);
handleResize();

// Render components grid
function renderComponentGrid() {
  componentGrid.innerHTML = '';
  const components = getAssemblyComponents(50);

  components.forEach((component) => {
    const item = document.createElement('button');
    item.className = 'component-item';
    item.title = component.name;

    // Create mini preview
    const preview = document.createElement('canvas');
    preview.width = 80;
    preview.height = 80;
    renderComponentPreview(preview, component);

    const label = document.createElement('span');
    label.textContent = component.name;
    label.className = 'component-label';

    item.appendChild(preview);
    item.appendChild(label);

    item.addEventListener('click', () => {
      document.querySelectorAll('.component-item').forEach((i) => i.classList.remove('active'));
      item.classList.add('active');
      selectedComponent = component;
    });

    componentGrid.appendChild(item);
  });
}

function renderComponentPreview(previewCanvas: HTMLCanvasElement, component: AssemblyComponent) {
  const pctx = previewCanvas.getContext('2d')!;
  pctx.fillStyle = '#1e293b';
  pctx.fillRect(0, 0, 80, 80);

  // Find bounds
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const t of component.triangles) {
    for (const v of t.vertices) {
      minX = Math.min(minX, v.x);
      maxX = Math.max(maxX, v.x);
      minY = Math.min(minY, v.y);
      maxY = Math.max(maxY, v.y);
    }
  }

  const width = maxX - minX;
  const height = maxY - minY;
  const scale = Math.min(60 / width, 60 / height);
  const offsetX = 40 - ((minX + maxX) / 2) * scale;
  const offsetY = 40 - ((minY + maxY) / 2) * scale;

  for (const t of component.triangles) {
    pctx.fillStyle = t.color;
    pctx.beginPath();
    pctx.moveTo(t.vertices[0].x * scale + offsetX, t.vertices[0].y * scale + offsetY);
    pctx.lineTo(t.vertices[1].x * scale + offsetX, t.vertices[1].y * scale + offsetY);
    pctx.lineTo(t.vertices[2].x * scale + offsetX, t.vertices[2].y * scale + offsetY);
    pctx.closePath();
    pctx.fill();
    pctx.strokeStyle = '#334155';
    pctx.lineWidth = 0.5;
    pctx.stroke();
  }
}

// Main render
function render() {
  const width = canvas.width / (window.devicePixelRatio || 1);
  const height = canvas.height / (window.devicePixelRatio || 1);

  // Clear
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(0, 0, width, height);

  // Draw grid guides
  drawGrid(width, height);

  // Draw placed components
  const triangles = assembly.getAllTriangles();
  for (const t of triangles) {
    drawTriangle(t, canvasCenter);
  }

  // Draw preview of component being placed
  if (selectedComponent) {
    const previewTriangles = selectedComponent.triangles.map((t) => {
      const rotated = rotateTriangle(t, { x: 0, y: 0 }, currentRotation);
      return translateTriangle(rotated, {
        x: mousePos.x - canvasCenter.x,
        y: mousePos.y - canvasCenter.y,
      });
    });

    ctx.globalAlpha = 0.6;
    for (const t of previewTriangles) {
      drawTriangle(t, canvasCenter);
    }
    ctx.globalAlpha = 1;

    // Draw rotation indicator
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.arc(mousePos.x, mousePos.y, 30, 0, Math.PI * 2);
    ctx.stroke();

    // Draw rotation arrow
    const arrowAngle = (currentRotation * Math.PI) / 180 - Math.PI / 2;
    ctx.beginPath();
    ctx.moveTo(mousePos.x, mousePos.y);
    ctx.lineTo(
      mousePos.x + Math.cos(arrowAngle) * 30,
      mousePos.y + Math.sin(arrowAngle) * 30
    );
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Update stats
  placedCount.textContent = assembly.getPlaced().length.toString();
}

function drawGrid(width: number, height: number) {
  ctx.strokeStyle = '#1e293b';
  ctx.lineWidth = 0.5;

  // Draw concentric hexagons as guides
  for (let r = 50; r < Math.max(width, height); r += 100) {
    ctx.beginPath();
    for (let i = 0; i <= 6; i++) {
      const angle = (i * 60 - 30) * (Math.PI / 180);
      const x = canvasCenter.x + Math.cos(angle) * r;
      const y = canvasCenter.y + Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  // Draw radial lines (60° apart)
  for (let i = 0; i < 6; i++) {
    const angle = (i * 60) * (Math.PI / 180);
    ctx.beginPath();
    ctx.moveTo(canvasCenter.x, canvasCenter.y);
    ctx.lineTo(
      canvasCenter.x + Math.cos(angle) * Math.max(width, height),
      canvasCenter.y + Math.sin(angle) * Math.max(width, height)
    );
    ctx.stroke();
  }

  // Center dot
  ctx.fillStyle = '#475569';
  ctx.beginPath();
  ctx.arc(canvasCenter.x, canvasCenter.y, 4, 0, Math.PI * 2);
  ctx.fill();
}

function drawTriangle(triangle: AssemblyTriangle, offset: { x: number; y: number }) {
  ctx.fillStyle = triangle.color;
  ctx.beginPath();
  ctx.moveTo(triangle.vertices[0].x + offset.x, triangle.vertices[0].y + offset.y);
  ctx.lineTo(triangle.vertices[1].x + offset.x, triangle.vertices[1].y + offset.y);
  ctx.lineTo(triangle.vertices[2].x + offset.x, triangle.vertices[2].y + offset.y);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = '#1e293b';
  ctx.lineWidth = 1;
  ctx.stroke();
}

// Mouse events
canvas.addEventListener('mousemove', (e) => {
  const rect = canvas.getBoundingClientRect();
  mousePos = {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top,
  };

  // Snap to existing vertices if enabled
  if (snapEnabled && selectedComponent) {
    const relPos = {
      x: mousePos.x - canvasCenter.x,
      y: mousePos.y - canvasCenter.y,
    };
    const snapPoint = assembly.findSnapPoint(relPos, 25);
    if (snapPoint) {
      mousePos = {
        x: snapPoint.x + canvasCenter.x,
        y: snapPoint.y + canvasCenter.y,
      };
    }
  }

  render();
});

canvas.addEventListener('click', () => {
  if (selectedComponent) {
    const position = {
      x: mousePos.x - canvasCenter.x,
      y: mousePos.y - canvasCenter.y,
    };
    assembly.place(selectedComponent, position, currentRotation);
    render();
  }
});

canvas.addEventListener('contextmenu', (e) => {
  e.preventDefault();
  // Remove last placed component
  const placed = assembly.getPlaced();
  if (placed.length > 0) {
    assembly.remove(placed[placed.length - 1].id);
    render();
  }
});

// Rotation slider
rotationSlider.addEventListener('input', () => {
  currentRotation = parseInt(rotationSlider.value, 10);
  rotationValue.textContent = `${currentRotation}°`;
  render();
});

// Quick rotation buttons via keyboard
document.addEventListener('keydown', (e) => {
  if (e.key === 'r' || e.key === 'R') {
    currentRotation = (currentRotation + 60) % 360;
    rotationSlider.value = currentRotation.toString();
    rotationValue.textContent = `${currentRotation}°`;
    render();
  }

  if (e.key === 'e' || e.key === 'E') {
    currentRotation = (currentRotation - 60 + 360) % 360;
    rotationSlider.value = currentRotation.toString();
    rotationValue.textContent = `${currentRotation}°`;
    render();
  }

  if (e.key === 'Escape') {
    selectedComponent = null;
    document.querySelectorAll('.component-item').forEach((i) => i.classList.remove('active'));
    render();
  }

  // Number keys for quick component selection
  if (e.key >= '1' && e.key <= '5') {
    const index = parseInt(e.key, 10) - 1;
    const items = document.querySelectorAll('.component-item');
    if (items[index]) {
      (items[index] as HTMLButtonElement).click();
    }
  }
});

// Snap toggle
snapToggle.addEventListener('change', () => {
  snapEnabled = snapToggle.checked;
});

// Clear button
clearBtn.addEventListener('click', () => {
  if (confirm('Clear all placed components?')) {
    assembly.clear();
    render();
  }
});

// Export SVG
exportSvgBtn.addEventListener('click', () => {
  const width = canvas.width / (window.devicePixelRatio || 1);
  const height = canvas.height / (window.devicePixelRatio || 1);
  const svg = assembly.exportSVG(width, height);

  const blob = new Blob([svg], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = 'trinity-assembly.svg';
  link.href = url;
  link.click();
  URL.revokeObjectURL(url);
});

// Initialize
renderComponentGrid();
render();

console.log('🔺 Trinity Assembly Mode');
console.log('R = Rotate +60°, E = Rotate -60°');
console.log('Click = Place, Right-click = Undo last');
console.log('1-5 = Select component');
